import {
  UrgencyDailyPointDTO,
  UrgencyDimensionRowDTO,
  UrgencyHourDTO,
  UrgencyKpisDTO,
  UrgencyLongestWaitDTO,
} from '../../dto/urgencySummary/UrgencySummaryDTO'
import { CRITICAL_HOURS_MIN_ATTENTIONS } from '../ServicesStatistics/alertThresholds'
import { markPeaks, PeakAnalysis } from '../ServicesStatistics/peaks'
import { SUSPECT_P90_FACTOR, SUSPECT_WAIT_MINUTES } from '../ServicesStatistics/periodDetail'

/**
 * Cálculos de la segunda tanda de «Atenciones de urgencia» (evolución, por hora, panel del día). Funciones puras, sin
 * React. Los picos y el aviso de atención sin cerrar usan las mismas reglas que Estadísticas de atención.
 */

/** Qué se mide en los gráficos: el mismo dato en otra vista. */
export type UrgencyMeasure = 'arrivals' | 'wait'

/** Las medidas en el orden en que se ofrecen en «Medir por». */
export const URGENCY_MEASURES: readonly UrgencyMeasure[] = ['arrivals', 'wait']

// ── Evolución ──

/**
 * Pacientes con espera medida que tiene que tener un día para que su espera media cuente para los picos (y no lleve el
 * marcador hueco de «pocos pacientes»). En Estadísticas de atención el piso es 10 atenciones; en la guardia se sube a 20
 * porque la espera de una guardia es muy despareja (el desvío es parecido a la media): con n pacientes el error de la
 * media es de más o menos desvío / raíz de n, es decir, unos 10 min sobre una media de 40 con n = 20 (25 %) y unos 14 min
 * con n = 10 (35 %), que ya no distingue un pico de un día común. Se aplica como piso del criterio relativo de
 * `markPeaks` (la cuarta parte de la mediana de pacientes por día), que en una guardia grande (cientos por día) es el que manda.
 */
export const URGENCY_PEAK_MIN_WAITED = 20

/** Un día cuenta como día con datos si tuvo llegadas: los días vacíos (sin datos) no se dibujan ni abren detalle. */
export const hasArrivals = (day: UrgencyDailyPointDTO): boolean => day.arrivals > 0

/**
 * Picos de la espera media diaria, uno por posición de `daily`. La cantidad que respalda a cada promedio son los pacientes
 * con espera medida (no las llegadas). Mínimo de 7 días que cuentan, hasta 3 picos, media + 2 desvíos (ver `markPeaks`).
 */
export function waitPeaks(daily: UrgencyDailyPointDTO[]): PeakAnalysis {
  return markPeaks(
    daily.map((day) => ({ value: hasArrivals(day) ? day.avgWaitMinutes : null, attentions: day.waitedCount })),
    URGENCY_PEAK_MIN_WAITED,
  )
}

/** Llegadas de un día promedio: sobre los días con llegadas (los días sin datos no bajan el promedio). Null si no hay ninguno. */
export function averageArrivalsPerDay(daily: UrgencyDailyPointDTO[]): number | null {
  const days = daily.filter(hasArrivals)
  return days.length === 0 ? null : days.reduce((sum, day) => sum + day.arrivals, 0) / days.length
}

// ── Panel del día: contra qué se compara ──

/**
 * Contra qué se compara lo que se mira en el panel del día: el promedio de todo el período de la pantalla o, mirando una
 * guardia, el promedio de esa guardia en el período. `nameId` es el texto que lo nombra («el promedio del período»).
 */
export interface UrgencyDayReference {
  nameId: string
  /** Llegadas de un día promedio de la referencia; null si no hay con qué comparar. */
  averageArrivals: number | null
  avgWaitMinutes: number | null
  waitOver60Pct: number | null
  leftWithoutAttentionPct: number | null
}

/** Un porcentaje (en %) con un decimal, como lo manda el backend. */
const round1 = (value: number): number => Math.round(value * 10) / 10

/** El promedio de todo el período de la pantalla (indicadores del `/summary` del período y sus días con llegadas). */
export function periodReference(kpis: UrgencyKpisDTO, daily: UrgencyDailyPointDTO[]): UrgencyDayReference {
  return {
    nameId: 'UrgencySummaryDayAverageName',
    averageArrivals: averageArrivalsPerDay(daily),
    avgWaitMinutes: kpis.avgWaitMinutes,
    waitOver60Pct: kpis.waitOver60Pct,
    leftWithoutAttentionPct: kpis.leftWithoutAttentionPct,
  }
}

/**
 * El promedio de una guardia en el período, a partir de su fila de «Por guardia» del `/summary` del período (el contexto
 * del período trae una fila por guardia con llegadas, espera media, atendidos con espera medida y esperaron más de 1 hora). Las
 * llegadas de un día promedio son las de la guardia sobre los días con llegadas del período (los días en que la guardia no
 * tuvo llegadas cuentan como cero). Sin la fila de la guardia, se compara contra el período entero y el texto lo dice así.
 */
export function templateReference(
  row: UrgencyDimensionRowDTO | undefined,
  period: UrgencyDayReference,
  activeDays: number,
): UrgencyDayReference {
  if (!row) return period
  return {
    nameId: 'UrgencySummaryDayTemplateAverageName',
    averageArrivals: activeDays > 0 ? row.arrivals / activeDays : null,
    avgWaitMinutes: row.waitedAvgMinutes,
    waitOver60Pct: row.waitedCount > 0 ? round1((row.waitOver60 / row.waitedCount) * 100) : null,
    leftWithoutAttentionPct: row.leftPercent,
  }
}

/** La guardia de ese día como un punto del gráfico: lo que se ve al instante en el título y los indicadores del panel. */
export function templateDayPoint(date: string, row: UrgencyDimensionRowDTO): UrgencyDailyPointDTO {
  return {
    date,
    arrivals: row.arrivals,
    attended: row.attended,
    leftWithoutAttention: row.leftWithoutAttention,
    avgWaitMinutes: row.waitedAvgMinutes,
    waitedCount: row.waitedCount,
    waitOver60: row.waitOver60,
  }
}

/** Las guardias con llegadas ese día, de más a menos llegadas (las «N/D» no se pueden filtrar). */
export function templatesOfDay(rows: UrgencyDimensionRowDTO[]): UrgencyDimensionRowDTO[] {
  return rows
    .filter((row) => row.id !== null && row.arrivals > 0)
    .sort((a, b) => b.arrivals - a.arrivals || a.name.localeCompare(b.name, 'es'))
}

// ── Por hora ──

/** Las horas de la franja en que se busca la espera más larga. */
export const WAIT_WINDOW_HOURS = 3

/**
 * Cada hora de la franja necesita al menos esta cantidad de pacientes con espera medida para que la franja cuente: es el
 * mismo mínimo que usan los «peores horarios» de Estadísticas de atención.
 */
export const HOURLY_MIN_WAITED = CRITICAL_HOURS_MIN_ATTENTIONS

/** Una franja de horas de llegada: `toHour` va de 1 a 24 (fin exclusivo) y con `fromHour >= toHour` cruza la medianoche. */
export interface HourWindow {
  fromHour: number
  toHour: number
  /** Espera media ponderada de la franja, en minutos. */
  avgWait: number
  /** Pacientes con espera medida de toda la franja. */
  waitedCount: number
}

export interface PeakHour {
  hour: number
  arrivals: number
  /** Parte de todas las llegadas, en %. */
  sharePct: number
}

/** Las 24 horas por posición (las que no vengan quedan en cero). */
function byHour(hours: UrgencyHourDTO[]): UrgencyHourDTO[] {
  return Array.from({ length: 24 }, (_, hour) => hours.find((item) => item.hour === hour)
    ?? { hour, arrivals: 0, attended: 0, leftWithoutAttention: 0, waitedCount: 0, avgWaitMinutes: null, waitOver60: 0 })
}

/** La hora con más llegadas (la más temprana si hay empate); null si no hubo llegadas. */
export function peakArrivalHour(hours: UrgencyHourDTO[]): PeakHour | null {
  const all = byHour(hours)
  const total = all.reduce((sum, item) => sum + item.arrivals, 0)
  if (total === 0) return null
  const best = all.reduce((top, item) => (item.arrivals > top.arrivals ? item : top), all[0])
  return { hour: best.hour, arrivals: best.arrivals, sharePct: (best.arrivals / total) * 100 }
}

/**
 * La franja de `WAIT_WINDOW_HOURS` horas seguidas de llegada con más espera media (ponderada por pacientes), en la que
 * cada hora tiene al menos `HOURLY_MIN_WAITED` pacientes con espera medida. Las franjas pueden cruzar la medianoche
 * (22, 23 y 0 h). Con empate gana la que empieza antes. Null si ninguna franja alcanza el mínimo.
 */
export function busiestWaitWindow(hours: UrgencyHourDTO[]): HourWindow | null {
  const all = byHour(hours)
  let best: HourWindow | null = null
  for (let start = 0; start < 24; start += 1) {
    const slice = Array.from({ length: WAIT_WINDOW_HOURS }, (_, offset) => all[(start + offset) % 24])
    if (slice.some((item) => item.waitedCount < HOURLY_MIN_WAITED || item.avgWaitMinutes === null)) continue
    const waitedCount = slice.reduce((sum, item) => sum + item.waitedCount, 0)
    const avgWait = slice.reduce((sum, item) => sum + (item.avgWaitMinutes ?? 0) * item.waitedCount, 0) / waitedCount
    if (best === null || avgWait > best.avgWait) {
      best = { fromHour: start, toHour: ((start + WAIT_WINDOW_HOURS - 1) % 24) + 1, avgWait, waitedCount }
    }
  }
  return best
}

/** La franja cruza la medianoche (la convención del backend: `fromHour >= toHour`). */
export const crossesMidnight = (fromHour: number, toHour: number): boolean => fromHour >= toHour

// ── Atención sin cerrar ──

export interface OpenAttentionNotice {
  /** La espera más larga del día, en minutos. */
  maxWaitMinutes: number
  /** La espera media sin esa atención. */
  averageWithoutIt: number
}

/**
 * Si una sola atención explica la espera media del día: más de 6 h (`SUSPECT_WAIT_MINUTES`) y mucho más que lo habitual del
 * día. Es la regla de Estadísticas de atención, pero `/summary` no manda el percentil 90 (ahí se compara con 3 veces el
 * P90): acá se compara con 3 veces la espera media sin esa atención, que es la que se puede calcular con los datos que
 * hay. La espera más larga sale de la primera fila de «Atenciones con más espera» (viene ordenada de mayor a menor). Sin
 * ese dato, o sin otro paciente con quien comparar, no hay aviso.
 */
export function openAttentionNoticeOf(kpis: UrgencyKpisDTO, longest: UrgencyLongestWaitDTO[]): OpenAttentionNotice | null {
  if (longest.length === 0 || kpis.avgWaitMinutes === null || kpis.waitedCount < 2) return null
  const maxWaitMinutes = longest.reduce((max, row) => Math.max(max, row.waitMinutes), 0)
  const averageWithoutIt = Math.max(0, (kpis.avgWaitMinutes * kpis.waitedCount - maxWaitMinutes) / (kpis.waitedCount - 1))
  if (!(maxWaitMinutes > SUSPECT_WAIT_MINUTES && maxWaitMinutes > SUSPECT_P90_FACTOR * averageWithoutIt)) return null
  return { maxWaitMinutes, averageWithoutIt }
}
